import type { ParsedLevel } from '../data/levels';
import { Grid } from './grid';
import { cellKey, type Cell, type WorldState } from './types';

// Rooms are smaller now and the campaign is played as a connected run, so a
// family is a handful of fruit per room rather than a whole screen of them.
export const MIN_FRUIT_PER_FAMILY = 6;
export const MAX_FRUIT_PER_FAMILY = 10;

export function fruitPerFamily(levelId: number): number {
  return Math.max(MIN_FRUIT_PER_FAMILY, Math.min(MAX_FRUIT_PER_FAMILY, 4 + levelId));
}

/**
 * Builds one deterministic route per fruit family. Campaign arenas have enough
 * safe cells for the routes to be mutually disjoint; the reuse fallback only
 * protects custom/debug maps whose total pool is smaller than every route.
 */
export function buildFruitRoutes(
  level: ParsedLevel,
  levelId: number,
  waveCount: number,
  /**
   * Optional grouping. In the connected world each wave belongs to one room,
   * so a route only ever draws from cells this returns its own index for.
   */
  partition?: (cell: Cell) => number,
): readonly (readonly Cell[])[] {
  const count = Math.max(0, Math.floor(waveCount));
  if (count === 0) return [];

  const allCandidates = buildCandidates(level);
  const candidates = allCandidates;
  const target = fruitPerFamily(levelId);
  const routes: Cell[][] = Array.from({ length: count }, () => []);
  const routeKeys = routes.map(() => new Set<string>());
  const globallySelected = new Set<string>();
  const candidateKeys = new Set(candidates.map(cellKey));

  // Preserve the hand-authored collection landmarks, distributing them across
  // families instead of repeating the same landmarks in every wave.
  let authoredIndex = 0;
  for (const authored of level.cores) {
    const key = cellKey(authored);
    if (!candidateKeys.has(key) || globallySelected.has(key)) continue;
    const routeIndex = partition ? partition(authored) : authoredIndex % count;
    if (routeIndex < 0 || routeIndex >= count) continue;
    addCell(routes[routeIndex]!, routeKeys[routeIndex]!, globallySelected, authored);
    authoredIndex += 1;
  }

  // Fill in rounds so earlier families cannot monopolize all of the most useful
  // cells. Farthest-point sampling creates a readable sweep around the arena.
  let madeProgress = true;
  while (madeProgress && routes.some((route) => route.length < target)) {
    madeProgress = false;
    for (let routeIndex = 0; routeIndex < routes.length; routeIndex += 1) {
      const route = routes[routeIndex]!;
      if (route.length >= target) continue;
      const pool = partition
        ? allCandidates.filter((cell) => partition(cell) === routeIndex)
        : candidates;
      const next = selectNextCell(
        pool,
        route,
        routeKeys[routeIndex]!,
        globallySelected,
        level.playerSpawns[0],
        levelId,
        routeIndex,
      );
      if (!next) continue;
      addCell(route, routeKeys[routeIndex]!, globallySelected, next);
      madeProgress = true;
    }
  }

  return routes.map((route) => route.map((cell) => ({ ...cell })));
}

function buildCandidates(level: ParsedLevel): Cell[] {
  const grid = new Grid(level.width, level.height);
  const normalReachable = grid.flood(level.playerSpawns[0], blockedInState(level, 'normal'));
  const overdriveReachable = grid.flood(level.playerSpawns[0], blockedInState(level, 'overdrive'));
  const excluded = new Set<string>([
    cellKey(level.exit),
    // A living fruit must never stand in the one-tile passage between rooms.
    // Keeping the seam clear makes forward and backward travel readable and
    // prevents a roaming fruit from visually plugging an open arch.
    ...level.doors.map(cellKey),
    ...level.playerSpawns.map(cellKey),
    ...level.enemies.map((enemy) => cellKey(enemy.cell)),
    ...(level.shard ? [cellKey(level.shard)] : []),
    ...(level.boss ? [cellKey(level.boss)] : []),
  ]);

  const candidates: Cell[] = [];
  for (let y = 1; y < level.height - 1; y += 1) {
    for (let x = 1; x < level.width - 1; x += 1) {
      const cell = { x, y };
      const key = cellKey(cell);
      if (excluded.has(key)
        || !normalReachable.has(key)
        || !overdriveReachable.has(key)
        || level.permanentWalls.has(key)
        || level.normalWalls.has(key)
        || level.overdriveWalls.has(key)
        || level.breakables.has(key)
        || level.gates.has(key)
        || level.shiftPads.has(key)
        || level.lasers.has(key)
        || level.fadingFloors.has(key)) continue;
      candidates.push(cell);
    }
  }
  return candidates;
}

function selectNextCell(
  candidates: readonly Cell[],
  route: readonly Cell[],
  currentRouteKeys: ReadonlySet<string>,
  globallySelected: ReadonlySet<string>,
  spawn: Cell,
  levelId: number,
  routeIndex: number,
): Cell | null {
  const unused = findBestCell(candidates, route, currentRouteKeys, globallySelected, spawn, levelId, routeIndex);
  if (unused) return unused;

  // A small custom map may not have enough cells for globally disjoint routes.
  // Reuse cells from earlier families, but never duplicate within one family.
  return findBestCell(candidates, route, currentRouteKeys, null, spawn, levelId, routeIndex);
}

function findBestCell(
  candidates: readonly Cell[],
  route: readonly Cell[],
  currentRouteKeys: ReadonlySet<string>,
  globalExclusion: ReadonlySet<string> | null,
  spawn: Cell,
  levelId: number,
  routeIndex: number,
): Cell | null {
  let best: Cell | null = null;
  let bestDistance = -1;
  let bestTie = -1;
  for (const candidate of candidates) {
    const key = cellKey(candidate);
    if (currentRouteKeys.has(key) || globalExclusion?.has(key)) continue;
    const distance = distanceFromRoute(candidate, route, spawn);
    const tie = deterministicTie(candidate, levelId, routeIndex, route.length);
    if (distance > bestDistance || (distance === bestDistance && tie > bestTie)) {
      best = candidate;
      bestDistance = distance;
      bestTie = tie;
    }
  }
  return best;
}

function distanceFromRoute(candidate: Cell, route: readonly Cell[], spawn: Cell): number {
  if (route.length === 0) return manhattan(candidate, spawn);
  let distance = Number.POSITIVE_INFINITY;
  for (const selected of route) distance = Math.min(distance, manhattan(candidate, selected));
  return distance;
}

function addCell(route: Cell[], routeKeys: Set<string>, globallySelected: Set<string>, cell: Cell): void {
  const key = cellKey(cell);
  route.push({ ...cell });
  routeKeys.add(key);
  globallySelected.add(key);
}

function blockedInState(level: ParsedLevel, state: WorldState): (cell: Cell) => boolean {
  return (cell) => {
    const key = cellKey(cell);
    return level.permanentWalls.has(key)
      || level.breakables.has(key)
      || (state === 'normal' ? level.normalWalls.has(key) : level.overdriveWalls.has(key));
  };
}

function manhattan(a: Cell, b: Cell): number {
  return Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
}

function deterministicTie(cell: Cell, levelId: number, routeIndex: number, selectionIndex: number): number {
  return (
    (cell.x + 1) * 73_856_093
    ^ (cell.y + 1) * 19_349_663
    ^ levelId * 83_492_791
    ^ (routeIndex + 1) * 2_654_435_761
    ^ (selectionIndex + 1) * 97_531
  ) >>> 0;
}
